import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from 'react';
import {
  BrowserRouter,
  Link as RouterLink,
  NavLink as RouterNavLink,
  Navigate as RouterNavigate,
  Route,
  Routes,
  useLocation as useRouterLocation,
  useNavigate as useRouterNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { parseTenantPath, withTenant, withTenantUrlRid } from '../utils/tenantPath';

import { setTabTenant } from '../utils/tabTenant';

const TenantUrlContext = createContext(null);

function tenantDestination(tenant, to) {
  if (tenant?.invalid) return withTenantUrlRid(tenant.urlRid, to);
  return withTenant(tenant?.rid, to);
}

export function TenantUrlProvider({ children }) {
  const location = useRouterLocation();
  const tenant = useMemo(() => parseTenantPath(location.pathname), [location.pathname]);
  // Remounting cannot stop awaited work in an old page. Commit Router history
  // (including state), then discard that document when leaving its tenant.
  const previousTenant = useRef(tenant.rid);
  const changingTenant = previousTenant.current != null && previousTenant.current !== tenant.rid;
  useLayoutEffect(() => {
    if (changingTenant) window.location.reload();
    else previousTenant.current = tenant.rid;
  }, [tenant.rid, changingTenant]);
  useEffect(() => {
    if (tenant.rid != null) setTabTenant(tenant.rid);
  }, [tenant.rid]);
  if (changingTenant) return null;
  return (
    <TenantUrlContext.Provider value={tenant}>
      {children}
    </TenantUrlContext.Provider>
  );
}

export function useTenantId() {
  return useContext(TenantUrlContext)?.rid ?? null;
}

export function useNavigate() {
  const navigate = useRouterNavigate();
  const tenant = useContext(TenantUrlContext);
  return useCallback((to, ...rest) => {
    navigate(tenantDestination(tenant, to), ...rest);
  }, [navigate, tenant]);
}

export const Link = forwardRef(function Link({ to, ...props }, ref) {
  const tenant = useContext(TenantUrlContext);
  return <RouterLink ref={ref} to={tenantDestination(tenant, to)} {...props} />;
});

export const NavLink = forwardRef(function NavLink({ to, ...props }, ref) {
  const tenant = useContext(TenantUrlContext);
  return <RouterNavLink ref={ref} to={tenantDestination(tenant, to)} {...props} />;
});

export function Navigate({ to, ...props }) {
  const tenant = useContext(TenantUrlContext);
  return <RouterNavigate to={tenantDestination(tenant, to)} {...props} />;
}

export function useLocation() {
  const location = useRouterLocation();
  return useMemo(() => {
    const { path, invalid } = parseTenantPath(location.pathname);
    if (invalid) return location;
    return path === location.pathname ? location : { ...location, pathname: path };
  }, [location]);
}

export function useRawLocation() {
  return useRouterLocation();
}

export function useTenantHref(path) {
  const tenant = useContext(TenantUrlContext);
  return tenantDestination(tenant, path);
}

export {
  BrowserRouter,
  Route,
  Routes,
  useParams,
  useSearchParams,
};
